/**
 * components/ui/error-state.tsx
 *
 * Inline error frame with retry CTA. Use this anywhere an async fetch or
 * derived-state computation can fail.
 */

import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}

export function ErrorState({
  title = 'Something didn\u2019t load',
  message,
  onRetry,
  retryLabel = 'Try again',
}: ErrorStateProps): React.JSX.Element {
  return (
    <div className="flex flex-col items-start gap-3 rounded-sm border border-ops-danger/30 bg-ops-danger/5 px-6 py-6">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-ops-danger" aria-hidden />
        <span className="font-mono text-2xs uppercase tracking-[0.18em] text-ops-danger">
          {title}
        </span>
      </div>
      <p className="text-sm text-foreground/85">{message}</p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}
